import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSameBedComposition, roomPriceWithoutSupplements } from '@koob/utils';
import { KoobAlert } from '@koob/ui';
import { RoomCard } from './RoomCards';
import CheckoutHotelRoom from '@koob/checkout/src/components/CheckoutHotelRoom';

export default function RoomItem({
  possibility,
  selectedPossibilityIndex,
  handleSelect,
  isSelected,
  isRoomSelected,
  filters,
  deleteRoom,
  promotions,
  selectedRoomsFromPromotionSelect,
  setSelectedPromotionSupplements,
  selectedPromotionSupplements,
  selectedRoomsCount,
  supplementKey,
  updateRoomSupplements,
  selectedRoom,
  bookedRoom,
}) {
  const { t } = useTranslation('booking');
  const [currentBedIndex, setCurrentBedIndex] = useState(0);

  const currentPossibility =
    selectedPossibilityIndex === undefined
      ? possibility
      : (possibility?.variants?.find(
          variant => variant.originalIndex === selectedPossibilityIndex,
        ) ?? possibility);

  const currentBed =
    possibility?.bed ?? currentPossibility?.bedChoices[currentBedIndex];

  // KD-3532 — the bed this card would submit is the one the booking already
  // holds: picking it is not a change, so the card is locked and says why
  // rather than leaving the user with a confirm button that never enables.
  const isBookedRoom = isSameBedComposition(
    { bedChoice: currentBed },
    bookedRoom,
  );

  const travelersCount = Math.max(
    filters?.rooms?.reduce((acc, room) => acc + room.adults + room.children, 0),
    1,
  );

  // The card quotes the ROOM, supplements excluded — those belong to the summary
  // total. `selectedRoom` is the room being chosen, handed down by the caller
  // that tracks it: a promotion is picked on a DIFFERENT possibility of the same
  // hotel room, one this card's own `possibility` never becomes, so it is the
  // only object that knows the price the room is now sold at.
  const total = roomPriceWithoutSupplements(
    selectedRoom ?? possibility,
    currentBed,
  );

  return (
    <CheckoutHotelRoom
      scope="to"
      room={currentPossibility}
      parentRoom={possibility}
      selectedRoomIndex={selectedPossibilityIndex}
      currentBed={currentBed}
      price={{
        total,
        currency: currentBed?.currency,
        travelersCount,
      }}
      isSelected={isSelected && !isBookedRoom}
      disabled={isBookedRoom}
      onSelect={index => handleSelect(index)}
      testId={`${currentPossibility?.name?.toLowerCase().split(' ').join('-')}-${selectedRoomsCount}`}
    >
      {isBookedRoom && (
        <KoobAlert variant="warning" icon="fa-triangle-exclamation">
          {t('sameRoomAsBooked')}
        </KoobAlert>
      )}

      <RoomCard
        possibility={currentPossibility}
        currentBed={currentBed}
        currentBedIndex={currentBedIndex}
        changeSelectedBed={bedIndex => {
          setCurrentBedIndex(bedIndex);
          handleSelect(possibility?.originalIndex, bedIndex);
        }}
        isSelected={isSelected}
        isRoomSelected={isRoomSelected}
        deleteRoom={deleteRoom}
        promotions={promotions}
        selectedRoomsFromPromotionSelect={selectedRoomsFromPromotionSelect}
        setSelectedPromotionSupplements={setSelectedPromotionSupplements}
        selectedPromotionSupplements={selectedPromotionSupplements}
        supplementKey={supplementKey}
        updateRoomSupplements={updateRoomSupplements}
      />
    </CheckoutHotelRoom>
  );
}
